/**
 * RelaySimulatorPage
 * Dev-only page for simulating relay inbound email flows.
 * Route: /relay/simulator
 *
 * Section 1: Compose cold inbound (no caseId)
 * Section 2: Cases with simulated traffic (filterable list)
 *
 * Production safety: redirects to /settings/agent-email when simulator is disabled.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FlaskConical, Search } from 'lucide-react';
import { PageHeader } from '../components/layout/index';
import { EmptyState } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { AgentUpgradePrompt } from '../components/billing/AgentUpgradePrompt';
import { useBillingFeatures } from '../hooks/useBilling';
import {
  fetchSimulatorEnabled,
  fetchSimulatedCases,
  postSimulatedColdInbound,
  type SimulatedCaseSummary,
  type RoutingResult,
} from '../lib/relay-simulator.api';

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

const FROM_ADDRESS_PATTERN = /@(test\.)?example\.com$/i;

function validateFromAddress(value: string): string | null {
  if (!value.trim()) return 'From address is required.';
  if (!FROM_ADDRESS_PATTERN.test(value)) {
    return 'From address must end with @example.com or @test.example.com.';
  }
  return null;
}

// Known pipeline domains accepted by the simulator backend
const SIMULATOR_RELAY_DOMAIN_PATTERN = /relay\.[a-z]+\.resida\.me$/i;
const SIMULATOR_LEADS_DOMAIN_PATTERN = /leads\.[a-z]+\.resida\.me$/i;

function validateToAddress(value: string): string | null {
  if (!value.trim()) return 'To address is required.';
  const atIndex = value.lastIndexOf('@');
  if (atIndex === -1) return 'To address must be a valid email.';
  const domain = value.substring(atIndex + 1).toLowerCase();

  // Relay domain: must have relay-format local part
  if (SIMULATOR_RELAY_DOMAIN_PATTERN.test(domain)) {
    const localPart = value.substring(0, atIndex).toLowerCase();
    if (!localPart.includes('+') && !localPart.startsWith('case-')) {
      return 'Relay address must contain + (participant-bound) or start with case- (case alias).';
    }
    return null;
  }

  // Registered pipeline domains (leads, future others): any local part accepted
  if (SIMULATOR_LEADS_DOMAIN_PATTERN.test(domain)) {
    return null;
  }

  return 'To address must be a relay or leads intake address on resida.me.';
}

// ---------------------------------------------------------------------------
// Cold Compose Form
// ---------------------------------------------------------------------------

interface ColdComposeFormProps {
  onResult: (result: RoutingResult) => void;
}

function ColdComposeForm({ onResult }: ColdComposeFormProps) {
  const [fromAddress, setFromAddress] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fromError, setFromError] = useState<string | null>(null);
  const [toError, setToError] = useState<string | null>(null);
  const [bodyError, setBodyError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const fromErr = validateFromAddress(fromAddress);
    const toErr = validateToAddress(toAddress);
    const bodyErr = !body.trim() ? 'Message body is required.' : null;

    setFromError(fromErr);
    setToError(toErr);
    setBodyError(bodyErr);

    if (fromErr || toErr || bodyErr) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const result = await postSimulatedColdInbound({
        fromAddress: fromAddress.trim(),
        toAddress: toAddress.trim(),
        subject: subject.trim() || undefined,
        content: { kind: 'text', body: body.trim() },
      });
      onResult(result);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to send inbound simulation.');
    } finally {
      setSubmitting(false);
    }
  }, [fromAddress, toAddress, subject, body, onResult]);

  return (
    <form
      data-testid="simulator-cold-compose-form"
      onSubmit={handleSubmit}
      className="space-y-4"
      aria-label="Compose cold inbound email"
      noValidate
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-1">
          <label htmlFor="cold-from-address" className="block text-sm font-medium text-foreground">
            From address <span aria-hidden="true" className="text-destructive">*</span>
          </label>
          <input
            id="cold-from-address"
            data-testid="simulator-cold-from-address"
            type="email"
            value={fromAddress}
            onChange={(e) => {
              setFromAddress(e.target.value);
              setFromError(null);
            }}
            placeholder="tenant@example.com"
            aria-required="true"
            aria-invalid={fromError != null ? 'true' : undefined}
            aria-describedby={fromError ? 'cold-from-error' : undefined}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring"
          />
          {fromError && (
            <p id="cold-from-error" data-testid="simulator-cold-from-error" className="text-xs text-destructive">
              {fromError}
            </p>
          )}
          <p className="text-xs text-muted-foreground">Must end with @example.com or @test.example.com</p>
        </div>

        <div className="space-y-1">
          <label htmlFor="cold-to-address" className="block text-sm font-medium text-foreground">
            To address <span aria-hidden="true" className="text-destructive">*</span>
          </label>
          <input
            id="cold-to-address"
            data-testid="simulator-cold-to-address"
            type="email"
            value={toAddress}
            onChange={(e) => {
              setToAddress(e.target.value);
              setToError(null);
            }}
            placeholder="case-abc@relay.staging.resida.me or listing-abc@leads.staging.resida.me"
            aria-required="true"
            aria-invalid={toError != null ? 'true' : undefined}
            aria-describedby={toError ? 'cold-to-error' : undefined}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring"
          />
          {toError && (
            <p id="cold-to-error" data-testid="simulator-cold-to-error" className="text-xs text-destructive">
              {toError}
            </p>
          )}
          <p className="text-xs text-muted-foreground">Relay alias (case- or +) or leads intake address</p>
        </div>

        <div className="space-y-1">
          <label htmlFor="cold-subject" className="block text-sm font-medium text-foreground">
            Subject
          </label>
          <input
            id="cold-subject"
            data-testid="simulator-cold-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Optional subject"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      <div className="space-y-1">
        <label htmlFor="cold-body" className="block text-sm font-medium text-foreground">
          Message body <span aria-hidden="true" className="text-destructive">*</span>
        </label>
        <textarea
          id="cold-body"
          data-testid="simulator-cold-body"
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            setBodyError(null);
          }}
          placeholder="Enter the email body text…"
          rows={4}
          aria-required="true"
          aria-invalid={bodyError != null ? 'true' : undefined}
          aria-describedby={bodyError ? 'cold-body-error' : undefined}
          className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring resize-none"
        />
        {bodyError && (
          <p id="cold-body-error" data-testid="simulator-cold-body-error" className="text-xs text-destructive">
            {bodyError}
          </p>
        )}
      </div>

      {submitError && (
        <div
          data-testid="simulator-cold-submit-error"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
        >
          {submitError}
        </div>
      )}

      <div>
        <Button
          type="submit"
          data-testid="simulator-cold-compose-submit"
          disabled={submitting}
        >
          {submitting ? 'Sending…' : 'Send inbound'}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Routing Result Display
// ---------------------------------------------------------------------------

function RoutingResultDisplay({ result }: { result: RoutingResult }) {
  if (result.routed === true) {
    return (
      <div
        data-testid="simulator-cold-result-routed"
        className="mt-4 p-3 bg-success/10 border border-success/30 rounded-md text-sm"
        role="status"
      >
        <span className="font-medium text-success">Routed to case</span>{' '}
        <Link
          to={`/relay/simulator/${result.caseId}`}
          data-testid="simulator-cold-result-case-link"
          className="underline font-mono text-foreground hover:text-primary"
        >
          {result.caseId}
        </Link>
      </div>
    );
  }

  if (!result.routed && !result.dropped) {
    return (
      <div
        data-testid="simulator-cold-result-inbox"
        className="mt-4 p-3 bg-warning/10 border border-warning/30 rounded-md text-sm"
        role="status"
      >
        <span className="font-medium text-warning">Not routed to a case</span>{' '}
        <Link
          to="/email"
          data-testid="simulator-cold-result-inbox-link"
          className="underline text-foreground hover:text-primary"
        >
          See it in the Email inbox
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid="simulator-cold-result-dropped"
      className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-sm"
      role="status"
    >
      <span className="font-medium text-destructive">Dropped</span>
      {result.dropReason && (
        <span className="text-muted-foreground ml-1">— {result.dropReason}</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Case list row
// ---------------------------------------------------------------------------

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function CaseRow({ c }: { c: SimulatedCaseSummary }) {
  return (
    <Link
      to={`/relay/simulator/${c.id}`}
      data-testid={`simulator-case-row-${c.id}`}
      className="flex items-center justify-between gap-4 p-3 rounded-md border border-border bg-card hover:bg-accent/50 transition-colors text-sm"
      aria-label={`Open case ${c.shortId}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="font-mono text-xs text-muted-foreground shrink-0">{c.shortId}</span>
        <span className="shrink-0 px-1.5 py-0.5 rounded text-xs bg-secondary text-secondary-foreground">
          {c.kind}
        </span>
        <span className="truncate text-foreground">{c.title ?? '(untitled)'}</span>
      </div>
      <div className="flex items-center gap-4 shrink-0 text-muted-foreground">
        <span className="text-xs">{c.simulatedMessageCount} msgs</span>
        <span className="text-xs hidden sm:inline">{formatDate(c.lastSimulatedAt)}</span>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// RelaySimulatorPage
// ---------------------------------------------------------------------------

export function RelaySimulatorPage() {
  const navigate = useNavigate();
  const { data: featuresData } = useBillingFeatures();
  const agentSupportOff = featuresData !== undefined && !featuresData.features.includes('agentic_support');
  const [cases, setCases] = useState<SimulatedCaseSummary[]>([]);
  const [filter, setFilter] = useState('');
  const [loadingCases, setLoadingCases] = useState(true);
  const [casesError, setCasesError] = useState<string | null>(null);
  const [routingResult, setRoutingResult] = useState<RoutingResult | null>(null);
  const [checkingEnabled, setCheckingEnabled] = useState(true);

  // Debounce timer ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Production-safety: check if simulator is enabled on mount
  useEffect(() => {
    let cancelled = false;
    fetchSimulatorEnabled().then((enabled) => {
      if (cancelled) return;
      setCheckingEnabled(false);
      if (!enabled) {
        navigate('/settings/agent-email', { replace: true });
      }
    });
    return () => { cancelled = true; };
  }, [navigate]);

  const loadCases = useCallback(async (filterValue: string) => {
    try {
      setLoadingCases(true);
      setCasesError(null);
      const data = await fetchSimulatedCases(filterValue || undefined);
      setCases(data);
    } catch (err) {
      setCasesError(err instanceof Error ? err.message : 'Failed to load cases.');
    } finally {
      setLoadingCases(false);
    }
  }, []);

  // Initial load (and reload when enabled check completes)
  useEffect(() => {
    if (!checkingEnabled) {
      loadCases('');
    }
  }, [checkingEnabled, loadCases]);

  const handleFilterChange = useCallback((value: string) => {
    setFilter(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadCases(value);
    }, 300);
  }, [loadCases]);

  if (checkingEnabled) {
    return (
      <div
        data-testid="relay-simulator-page"
        className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]"
      >
        <div className="space-y-3 mt-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="relay-simulator-page"
      className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]"
    >
      <PageHeader
        title="Relay Simulator"
        subtitle="Dev-only tool for simulating inbound email flows"
      />

      {agentSupportOff && (
        <div className="mb-6">
          <AgentUpgradePrompt title="Agent support is off" />
        </div>
      )}

      {/* Section 1: Cold compose */}
      <section className="mb-6 border border-border rounded-lg p-4 md:p-6 bg-card">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-4">
          Compose cold inbound
        </h2>
        <ColdComposeForm onResult={setRoutingResult} />
        {routingResult && <RoutingResultDisplay result={routingResult} />}
      </section>

      {/* Section 2: Cases with simulated traffic */}
      <section className="border border-border rounded-lg p-4 md:p-6 bg-card">
        <div className="flex items-center justify-between mb-4 gap-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Cases with simulated traffic
          </h2>
        </div>

        {/* Filter input */}
        <div className="relative mb-4">
          <label htmlFor="simulator-case-filter" className="sr-only">
            Filter cases
          </label>
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
            aria-hidden="true"
          />
          <input
            id="simulator-case-filter"
            data-testid="simulator-case-filter"
            type="search"
            value={filter}
            onChange={(e) => handleFilterChange(e.target.value)}
            placeholder="Filter by short ID, title, or kind…"
            className="w-full pl-9 pr-3 py-1.5 rounded-md border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-ring"
            aria-label="Filter cases by short ID, title, or kind"
          />
        </div>

        {casesError && (
          <div
            data-testid="simulator-cases-error"
            className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
            role="alert"
          >
            <span>{casesError}</span>
            <Button variant="outline" size="sm" onClick={() => loadCases(filter)}>
              Retry
            </Button>
          </div>
        )}

        {loadingCases && (
          <div data-testid="simulator-cases-loading" className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}

        {!loadingCases && !casesError && (
          <>
            {cases.length === 0 ? (
              <EmptyState
                icon={FlaskConical}
                title="No cases with simulated traffic"
                description="Use the form above to send a cold inbound message, or send a reply from the case detail page."
                data-testid="simulator-empty-state"
              />
            ) : (
              <div data-testid="simulator-case-list" className="space-y-2">
                {cases.map((c) => (
                  <CaseRow key={c.id} c={c} />
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
